import { useState, useEffect } from 'react';
import {formatTimeAgo } from '../../utils/formatters';

const AlertNotification = ({
  type = 'info',
  title,
  message,
  actions,
  dismissible = true,
  autoClose = false,
  autoCloseDelay = 5000,
  onClose,
  className = '',
  position = 'top-right',
  showIcon = true,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (autoClose && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose && onClose();
    }, 200);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        );
      case 'error':
      case 'critical':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        );
    }
  };

  const alertClasses = [
    'alert-notification',
    `alert-${type}`,
    `alert-position-${position}`,
    isClosing && 'alert-closing',
    className
  ].filter(Boolean).join(' ');

  if (!isVisible) return null;

  return (
    <div className={alertClasses} {...props}>
      {showIcon && (
        <div className="alert-icon">
          {getIcon()}
        </div>
      )}
      
      <div className="alert-content">
        {title && (
          <div className="alert-title">
            {title}
          </div>
        )}
        {message && (
          <div className="alert-message">
            {message}
          </div>
        )}
        {actions && (
          <div className="alert-actions">
            {actions}
          </div>
        )}
      </div>
      
      {dismissible && (
        <button 
          className="alert-close"
          onClick={handleClose}
          aria-label="Close alert"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      )}
    </div>
  );
};

// Alert list component for managing multiple alerts
export const AlertList = ({ alerts = [], position = 'top-right', maxVisible = 5 }) => {
  const [visibleAlerts, setVisibleAlerts] = useState([]);

  useEffect(() => {
    setVisibleAlerts(alerts.slice(-maxVisible));
  }, [alerts, maxVisible]);

  const handleRemoveAlert = (alertId) => {
    setVisibleAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const containerClasses = [
    'alert-list',
    `alert-list-${position}`
  ].join(' ');

  return (
    <div className={containerClasses}>
      {visibleAlerts.map((alert) => (
        <AlertNotification
          key={alert.id}
          {...alert}
          onClose={() => handleRemoveAlert(alert.id)}
        />
      ))}
    </div>
  );
};

// Real-time alert component for surveillance data
export const SurveillanceAlert = ({
  alert,
  onDismiss,
  onViewDetails,
  onMarkInvestigated,
  showActions = true,
  ...props
}) => {
  const getRiskLevel = (alertLevel) => {
    switch (alertLevel?.toUpperCase()) {
      case 'CRITICAL':
        return 'critical';
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      default:
        return 'info';
    }
  };

  const formatAlertMessage = (alert) => {
    const parts = [];
    
    if (alert.mutation_name) {
      parts.push(`Mutation ${alert.mutation_name} detected`);
    }
    
    if (alert.country) {
      parts.push(`in ${alert.country}`);
    }
    
    if (alert.host) {
      parts.push(`(${alert.host})`);
    }
    
    return parts.join(' ');
  };

  return (
    <AlertNotification
      type={getRiskLevel(alert.alert_level)}
      title={`${alert.alert_level} Alert`}
      message={formatAlertMessage(alert)}
      showIcon={true}
      dismissible={true}
      onClose={onDismiss}
      actions={
        showActions && (
          <div className="surveillance-alert-actions">
            {alert.detection_date && (
              <span className="alert-timestamp">
                {formatTimeAgo(alert.detection_date)}
              </span>
            )}
            {onViewDetails && (
              <button 
                className="btn btn-sm btn-outline"
                onClick={() => onViewDetails(alert)}
              >
                View Details
              </button>
            )}
            {onMarkInvestigated && alert.requires_investigation === 'True' && (
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => onMarkInvestigated(alert)}
              >
                Mark Investigated
              </button>
            )}
          </div>
        )
      }
      {...props}
    />
  );
};

// Banner alert for system-wide notifications
export const BannerAlert = ({
  children,
  type = 'info',
  dismissible = false,
  sticky = false,
  className = '',
  ...props
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const bannerClasses = [
    'banner-alert',
    `banner-alert-${type}`,
    sticky && 'banner-alert-sticky',
    className
  ].filter(Boolean).join(' ');

  if (isDismissed) return null;

  return (
    <div className={bannerClasses} {...props}>
      <div className="banner-alert-content">
        {children}
      </div>
      {dismissible && (
        <button 
          className="banner-alert-close"
          onClick={() => setIsDismissed(true)}
          aria-label="Dismiss banner"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      )}
    </div>
  );
};

// Toast notification hook for programmatic alerts
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    const newToast = { id, ...toast };
    setToasts(prev => [...prev, newToast]);
    
    if (toast.autoClose !== false) {
      setTimeout(() => {
        removeToast(id);
      }, toast.autoCloseDelay || 5000);
    }
    
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const success = (message, options = {}) => {
    return addToast({ type: 'success', message, ...options });
  };

  const error = (message, options = {}) => {
    return addToast({ type: 'error', message, ...options });
  };

  const warning = (message, options = {}) => {
    return addToast({ type: 'warning', message, ...options });
  };

  const info = (message, options = {}) => {
    return addToast({ type: 'info', message, ...options });
  };

  const ToastContainer = ({ position = 'top-right' }) => (
    <AlertList alerts={toasts} position={position} />
  );

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    ToastContainer
  };
};

export default AlertNotification;